// components/layout/Header.tsx
import React, { JSX, useState } from 'react';
import { NavigateFunction, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Tooltip,
  Button,
  IconButton,
  Box,
  Drawer,
  Divider,
  Avatar,
  Container,
  Fade,
  Popover,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Logout,
  ExpandMore,
  ExpandLess,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { getUserMenuItemsByGroup } from 'config/navigationConfig';
import { NavigationItem } from 'types/navigation';
import { Beta } from 'components/ui/Beta';
import { CopyBubble } from 'components/CopyBubble';

import 'components/layout/Header.css';
import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';

// Styled components
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: prop => prop !== 'transparent',
})<{ transparent?: boolean }>(({ theme, transparent }) => ({
  backgroundColor: transparent ? 'transparent' : theme.palette.primary.main,
  boxShadow: transparent ? 'none' : '',
  transition: 'background-color 0.3s ease',
  borderRadius: 0,
  padding: 0,
}));

const NavLinksContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flex: 1,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const UserActionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const UserButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 320,
    backgroundColor: theme.palette.background.paper,
  },
}));

const DropdownButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  textTransform: 'none',
  minHeight: 48,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const UserMenuContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[8],
  overflow: 'hidden',
  minWidth: 200,
}));

interface HeaderProps {
  transparent?: boolean;
  className?: string;
  navigate: NavigateFunction;
  navigationItems: NavigationItem[];
  currentPath: string;
  sessionId?: string | null;
}

type NavigationMenuItem = {
  id: string;
  label: string;
  icon: React.ReactElement | null;
  action: () => void;
  group?: string;
};

const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
  const { user, logout } = useAuth();
  const { setSnack } = useAppState();
  const { transparent = false, className, navigate, navigationItems, sessionId } = props;
  const theme = useTheme();
  const location = useLocation();

  const name = user?.firstName || user?.email || '';

  // State for desktop dropdown menus
  const [dropdownAnchors, setDropdownAnchors] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  // State for mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<{
    [key: string]: boolean;
  }>({});

  // State for user menu
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  const isAdmin = user?.isAdmin || false;

  // Get user menu items from navigation config
  const userMenuGroups = getUserMenuItemsByGroup(user?.userType || null, isAdmin);

  // Create user menu items array with proper actions
  const createUserMenuItems = (): NavigationMenuItem[] => {
    const items: Array<NavigationMenuItem> = [];

    // Add profile group items
    userMenuGroups.profile.forEach(item => {
      if (!item.divider) {
        items.push({
          id: item.id,
          label: item.label as string,
          icon: item.icon || null,
          action: () => item.path && navigate(item.path.replace(/:.*$/, ''), { replace: true }),
          group: 'profile',
        });
      }
    });

    // Add divider if we have items before system group
    if (items.length > 0 && userMenuGroups.system.length > 0) {
      items.push({
        id: 'divider',
        label: '',
        icon: null,
        action: () => {
          console.log('Divider clicked');
        },
        group: 'divider',
      });
    }

    // Add account group items
    userMenuGroups.account.forEach(item => {
      if (!item.divider) {
        items.push({
          id: item.id,
          label: item.label as string,
          icon: item.icon || null,
          action: () => {
            item.path && navigate(item.path.replace(/:.*$/, ''));
          },
          group: 'account',
        });
      }
    });

    // Add divider if we have items before system group
    if (items.length > 0 && userMenuGroups.system.length > 0) {
      items.push({
        id: 'divider',
        label: '',
        icon: null,
        action: () => {
          console.log('Divider clicked');
        },
        group: 'divider',
      });
    }

    // Add admin group items
    userMenuGroups.admin.forEach(item => {
      if (!item.divider) {
        items.push({
          id: item.id,
          label: item.label as string,
          icon: item.icon || null,
          action: () => {
            item.path && navigate(item.path.replace(/:.*$/, ''));
          },
          group: 'admin',
        });
      }
    });

    // Add divider if we have items before system group
    if (items.length > 0 && userMenuGroups.admin.length > 0) {
      items.push({
        id: 'divider',
        label: '',
        icon: null,
        action: () => {
          console.log('Divider clicked');
        },
        group: 'divider',
      });
    }

    // Add system group items with special handling for logout
    userMenuGroups.system.forEach(item => {
      if (item.id === 'logout') {
        items.push({
          id: 'logout',
          label: 'Logout',
          icon: <Logout fontSize="small" />,
          action: () => {
            logout();
            navigate('/');
          },
          group: 'system',
        });
      } else if (!item.divider) {
        items.push({
          id: item.id,
          label: item.label as string,
          icon: item.icon || null,
          action: () => {
            item.path && navigate(item.path.replace(/:.*$/, ''));
          },
          group: 'system',
        });
      }
    });

    // Add other group items
    userMenuGroups.other.forEach(item => {
      if (!item.divider) {
        items.push({
          id: item.id,
          label: item.label as string,
          icon: item.icon || null,
          action: () => item.path && navigate(item.path.replace(/:.*$/, '')),
          group: 'other',
        });
      }
    });

    return items;
  };

  const userMenuItems = createUserMenuItems();

  // Helper function to check if current path matches navigation item
  const isCurrentPath = (item: NavigationItem): boolean => {
    if (!item.path) return false;
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  // Helper function to check if any child is current path
  const hasActiveChild = (item: NavigationItem): boolean => {
    if (!item.children) return false;
    return item.children.some(child => isCurrentPath(child) || hasActiveChild(child));
  };

  // Desktop dropdown handlers
  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>, itemId: string): void => {
    setDropdownAnchors(prev => ({ ...prev, [itemId]: event.currentTarget }));
  };

  const handleDropdownClose = (itemId: string): void => {
    setDropdownAnchors(prev => ({ ...prev, [itemId]: null }));
  };

  // Mobile accordion handlers
  const handleMobileToggle = (itemId: string): void => {
    setMobileExpanded(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = (): void => {
    setUserMenuAnchor(null);
  };

  const handleUserMenuAction = (item: {
    id: string;
    label: string;
    icon: React.ReactElement | null;
    action: () => void;
    group?: string;
  }): void => {
    if (item.group !== 'divider') {
      item.action();
      handleUserMenuClose();
    }
  };

  // Navigation handlers
  const handleNavigate = (path: string): void => {
    navigate(path.replace(/:.*$/, ''));
    setMobileOpen(false);
    // Close all dropdowns
    setDropdownAnchors({});
  };

  // Render desktop navigation with dropdowns
  const renderDesktopNavigation = (): JSX.Element => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {navigationItems.map((item, index) => {
          const hasChildren = item.children && item.children.length > 0;
          const isActive = isCurrentPath(item) || hasActiveChild(item);

          if (hasChildren) {
            return (
              <Box
                key={item.id}
                sx={{
                  mr: index === 0 || index === navigationItems.length - 1 ? 'auto' : 'unset',
                }}
              >
                <DropdownButton
                  onClick={(e): void => handleDropdownOpen(e, item.id)}
                  endIcon={<KeyboardArrowDown />}
                  sx={{
                    backgroundColor: isActive ? 'action.selected' : 'transparent',
                    color: isActive ? 'secondary.main' : 'primary.contrastText',
                  }}
                >
                  {item.icon && <Box sx={{ mr: 1, display: 'flex' }}>{item.icon}</Box>}
                  {item.label}
                </DropdownButton>
                <Menu
                  anchorEl={dropdownAnchors[item.id]}
                  open={Boolean(dropdownAnchors[item.id])}
                  onClose={(): void => handleDropdownClose(item.id)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  TransitionComponent={Fade}
                >
                  {item.children?.map(child => (
                    <MenuItem
                      key={child.id}
                      onClick={(): void => {
                        child.path && handleNavigate(child.path);
                      }}
                      selected={isCurrentPath(child)}
                      disabled={!child.path}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '& *': { m: 0, p: 0 },
                        m: 0,
                      }}
                    >
                      {child.icon && <ListItemIcon>{child.icon}</ListItemIcon>}
                      <ListItemText>{child.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            );
          } else {
            return (
              <DropdownButton
                key={item.id}
                onClick={(): void => {
                  item.path && handleNavigate(item.path);
                }}
                sx={{
                  backgroundColor: isActive ? 'action.selected' : 'transparent',
                  color: isActive ? 'secondary.main' : 'primary.contrastText',
                  mr: index === 0 || index === navigationItems.length - 1 ? 'auto' : 'unset',
                }}
              >
                {item.icon && <Box sx={{ mr: 1, display: 'flex' }}>{item.icon}</Box>}
                {item.label}
              </DropdownButton>
            );
          }
        })}
      </Box>
    );
  };

  // Render mobile accordion navigation
  const renderMobileNavigation = (): JSX.Element => {
    const renderNavigationItem = (item: NavigationItem, depth = 0): JSX.Element => {
      const hasChildren = item.children && item.children.length > 0;
      const isActive = isCurrentPath(item) || hasActiveChild(item);
      const isExpanded = mobileExpanded[item.id];

      return (
        <Box key={item.id}>
          <ListItem disablePadding sx={{ pl: depth * 2 }}>
            <ListItemButton
              onClick={(): void => {
                if (hasChildren) {
                  handleMobileToggle(item.id);
                } else if (item.path) {
                  handleNavigate(item.path);
                }
              }}
              selected={isActive}
              sx={{
                backgroundColor: isActive ? 'action.selected' : 'transparent',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
              }}
            >
              {item.icon && <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>}
              <ListItemText
                primary={item.label}
                sx={{
                  '& .MuiTypography-root': {
                    fontSize: depth > 0 ? '0.875rem' : '1rem',
                    fontWeight: depth === 0 ? 500 : 400,
                  },
                }}
              />
              {hasChildren && (
                <IconButton size="small">{isExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
              )}
            </ListItemButton>
          </ListItem>
          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List disablePadding>
                {item.children?.map(child => renderNavigationItem(child, depth + 1))}
              </List>
            </Collapse>
          )}
        </Box>
      );
    };

    return (
      <List sx={{ pt: 0 }}>
        {navigationItems.map(item => renderNavigationItem(item))}
        <Divider sx={{ my: 1 }} />
        {!user && (
          <ListItem disablePadding>
            <ListItemButton onClick={(): void => handleNavigate('/login')}>
              <ListItemText primary="Login" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    );
  };

  // Render user menu content
  const renderUserMenu = (): JSX.Element => {
    return (
      <UserMenuContainer>
        <List dense>
          {userMenuItems.map((item, index) =>
            item.group === 'divider' ? (
              <Divider key={`divider-${index}`} />
            ) : (
              <ListItem key={item.id}>
                <ListItemButton onClick={(): void => handleUserMenuAction(item)}>
                  {item.icon && <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>}
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            )
          )}
        </List>
      </UserMenuContainer>
    );
  };

  // Render user account section
  const renderUserSection = (): JSX.Element => {
    if (!user) {
      return (
        <Button
          color="info"
          variant="contained"
          onClick={(): void => {
            navigate('/login');
          }}
          sx={{
            display: { xs: 'none', sm: 'block' },
            color: theme.palette.primary.contrastText,
          }}
        >
          Login
        </Button>
      );
    }

    return (
      <>
        <UserButton
          onClick={handleUserMenuOpen}
          aria-controls={userMenuOpen ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={userMenuOpen ? 'true' : undefined}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: theme.palette.secondary.main,
            }}
          >
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{name}</Box>
          <ExpandMore fontSize="small" />
        </UserButton>

        <Popover
          id="user-menu"
          open={userMenuOpen}
          anchorEl={userMenuAnchor}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          TransitionComponent={Fade}
        >
          {renderUserMenu()}
        </Popover>
      </>
    );
  };

  return (
    <StyledAppBar
      position="fixed"
      transparent={transparent}
      className={className}
      sx={{ overflow: 'hidden' }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Navigation Links - Desktop */}
          <NavLinksContainer>{renderDesktopNavigation()}</NavLinksContainer>

          {/* User Actions Section */}
          <UserActionsContainer>
            {renderUserSection()}

            {/* Mobile Menu Button */}
            <Tooltip title="Open Menu">
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>

            {sessionId && (
              <CopyBubble
                tooltip="Copy link"
                color="inherit"
                aria-label="copy link"
                edge="end"
                sx={{
                  width: 36,
                  height: 36,
                  opacity: 1,
                  bgcolor: 'inherit',
                  '&:hover': { bgcolor: 'action.hover', opacity: 1 },
                }}
                content={`${window.location.origin}${window.location.pathname}?id=${sessionId}`}
                onClick={(): void => {
                  navigate(`${window.location.pathname}?id=${sessionId}`);
                  setSnack('Link copied!');
                }}
                size="large"
              />
            )}
          </UserActionsContainer>

          {/* Mobile Navigation Drawer */}
          <MobileDrawer
            variant="temporary"
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
          >
            {renderMobileNavigation()}
          </MobileDrawer>
        </Toolbar>
      </Container>
      <Beta
        sx={{ left: '-130px', '& .mobile': { right: '0px' } }}
        onClick={(): void => {
          navigate('/');
        }}
      />
    </StyledAppBar>
  );
};

export { Header };
